/**
 * useGlobalActivitySeed Hook
 *
 * TanStack Query wrapper for the global-activity edge function.
 * Provides stale-while-revalidate caching so the activity feed
 * can show cached data instantly on subsequent loads.
 */

import { useQuery } from '@tanstack/react-query';
import { CACHE_TIERS, queryKeys } from '@/lib/queryClient';
import type { ActivityEvent, GlobalActivityResponse } from '@/types/activity';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

async function fetchGlobalActivity(
  limit: number,
): Promise<{ events: ActivityEvent[]; source: 'live' | 'historical' | 'mixed' }> {
  if (!SUPABASE_URL) {
    return { events: [], source: 'live' };
  }

  const url = `${SUPABASE_URL}/functions/v1/global-activity?limit=${limit}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      apikey: SUPABASE_ANON_KEY,
    },
  });

  if (!response.ok) {
    throw new Error(`global-activity API error: ${response.status}`);
  }

  const data: GlobalActivityResponse = await response.json();
  return { events: data.events || [], source: data.source || 'live' };
}

export function useGlobalActivitySeed(limit: number = 30) {
  return useQuery({
    queryKey: [...queryKeys.activity.history, 'seed', limit],
    queryFn: () => fetchGlobalActivity(limit),
    ...CACHE_TIERS.TIER_2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}
